import { createSlice } from "@reduxjs/toolkit";
import { storageLoad, storageSave, storageDelete } from "../../utils/localStorage";
import { CURRENT_LOAN_STORAGE_KEY } from "../../utils/aura.constants";

// Initial productInputs for a new current loan. Kept separate from aura.constants
// PRODUCT_INPUTS_DEFAULTS on purpose: this copy has no property fields, and it is the shape
// persisted under CURRENT_LOAN_STORAGE_KEY, so merging the two would change saved state.
const PRODUCT_INPUTS_DEFAULTS = {
	incomeVerificationType: "", debtToIncomeRatio: "", debtServiceCoverageRatio: "",
	loanPurpose: "", purchasePrice: "", estimatedValue: "", loanAmount: "",
	closedEndSecondLienAmt: "", helocLineAmount: "", helocDrawnAmount: "",
	cashoutAmount: "", ltv: "", cltv: "", hcltv: "",
	interestOnly: "", prePaymentPenalty: "", escrowWaiver: "",
	citizenship: "", firstTimeHomeBuyer: "", nonoccupantCoborrower: "No",
	selfEmployed: "", reserve: "", financedProperties: "",
	qualifyingCreditScore: "", housingEventType: "", housingEventSeasoning: "",
	bankruptcyType: "", bankruptcyOutcome: "", bankruptcySeasoning: "",
	mortgageLatePayments: "No",
	Mtg_Lates_x30_12: "", Mtg_Lates_x30_13_24: "",
	Mtg_Lates_x60_12: "", Mtg_Lates_x60_13_24: "",
	Mtg_Lates_x90_12: "", Mtg_Lates_x90_13_24: "",
	Mtg_Lates_x120_12: "", Mtg_Lates_x120_13_24: "",
	loanTerms: [], amortizationTypes: [], ARMFixedTerms: [],
	desiredRate: "", desiredPrice: 100.0, desiredLockPeriod: "30",
	lenderPaidvsBorrowerPaid: "",
	productTypes: ["StandardProducts", "ExpandedGuidelines"],
	loanType: ["Conforming", "NonConforming", "Conventional"],
	piLienType: "First",
};

const defaultState = {
	loanGuid: null,
	loanNumber: null,
	borrowerName: null,
	currentStage: "upload",
	selectedPath: null,
	qualifySubStep: null,
	auraResult: null,
	auraRunAt: null,
	// Set once Dashboard's getCurrentLoanProgress fetch has resolved (success or
	// 404 — either way, the attempt is done). useSyncAuraProgress must not PATCH
	// AURA-level state until this is true, otherwise resumeLoan's blank reset
	// values can race the hydration GET and overwrite real saved progress on the
	// backend with blanks.
	progressHydrated: false,
	selectedProgram: null,
	lockDecision: null,
	ficoScore: null,
	exceptionStatus: "none",
	// Last-known-good Loan Originator/Loan Processor selection for this loan, mirrored
	// here whenever Dashboard resolves or changes them (see the dashboard/index.jsx
	// effects that dispatch setLoanOriginatorId/setLoanProcessorId). Encompass's own
	// getLoan()/getBorrowerPair() read-back for these ids isn't fully reliable, so this
	// acts as a local cache of "the value that was correct last time we knew it" —
	// restored on mount instead of starting blank and waiting on a fresh Encompass fetch.
	loanOriginatorId: null,
	loanProcessorId: null,
	// Backend-persisted (via ClearPathSession's existing, previously-unused `progress_data`
	// JSON field — no backend change needed) home for Dashboard fields that have NO
	// confirmed real Encompass field anywhere in either repo, so they can never round-trip
	// through Encompass no matter what: propertyIncomeExtra.{sourceOfRents,leaseInPlace}
	// (DSCR "Subject property income," loan-level) and declarationsExtra[pairId].federalDelinquent180
	// (per-borrower-pair, keyed by pairId since a loan can have up to 3 pairs). Without
	// this, these fields silently reset to their defaults on every Dashboard remount —
	// for federalDelinquent180 that default is "No", so it doesn't even show as
	// incomplete, it just silently shows a possibly-wrong answer to a compliance-relevant
	// question. This is a deliberate, reversible stopgap: swap to a real Encompass field
	// later (once a confirmed field ID exists) without changing anything else.
	progressData: {},
	// Single authoritative Income Documentation Type — set on Dashboard, synced
	// (read-only) into Qualify's productInputs.incomeVerificationType. Gates Qualify.
	incomeDocumentationType: "",
	// Legacy mock-conditions slot, kept for saved-progress compatibility. Step D and
	// Submit now read real conditions from GET aura-conditions (LC-696).
	conditions: [],
	applicationDataChangedSinceAura: false,
	// Liabilities saved after AURA ran (PathA-2.09 "Run AURA again" note).
	liabilitiesChangedSinceAura: false,
	// Session-only (never persisted): the Dashboard has unsaved changes, and a pending
	// "save, then go to `target`" request from the left-nav save prompt (PathA-2.11).
	dashboardDirty: false,
	dashboardSaveRequest: null,
	sectionStatuses: {
		borrower: "incomplete",
		address: "incomplete",
		employment: "incomplete",
		assets: "incomplete",
		declarations: "incomplete",
		closing: "incomplete",
	},
	visitedStages: [],
	lastSavedAt: null,
	qualifyInputs: {
		creditVendor: "",
		reportRefId: "",
		reportOn: "",
		clientId: "",
		subscriberId: "",
		loginId: "",
		productCode: "",
		providerId: "",
		incomeDocType: "",
		lienType: "",
		lockTerm: "",
		compType: "",
		lpcPercent: "",
		lockPeriod: "",
		lockDecisionVal: "",
		// XML-derived display fields — persisted so they survive session navigation
		xmlFirstName: "",
		xmlLastName: "",
		xmlDob: "",
		xmlSsn: "",
		xmlLoanPurpose: "",
		xmlLoanAmount: "",
		xmlPurchasePrice: "",
		xmlAppraisedValue: "",
		xmlPropertyType: "",
		xmlOccupancy: "",
		xmlState: "",
		xmlCounty: "",
		xmlCity: "",
		xmlZip: "",
		xmlLoanAmountRaw: "0",
		xmlAppraisedValueRaw: "0",
	},
	productInputs: { ...PRODUCT_INPUTS_DEFAULTS },
	// Session-only — not persisted to localStorage
	xmlApplicationData: null,
};

const initialState = storageLoad(CURRENT_LOAN_STORAGE_KEY) || defaultState;

const persist = (state) => {
	// Exclude xmlApplicationData — it's session-only and can be large
	const {
		xmlApplicationData: _omit,
		dashboardDirty: _dirty,
		dashboardSaveRequest: _request,
		...persisted
	} = state;
	storageSave(CURRENT_LOAN_STORAGE_KEY, JSON.parse(JSON.stringify(persisted)));
};

const currentLoanSlice = createSlice({
	name: "currentLoan",
	initialState,
	reducers: {
		initLoan: (state, action) => {
			const { loanGuid, borrowerName, xmlApplicationData } = action.payload;
			Object.assign(state, {
				...defaultState,
				loanGuid,
				borrowerName,
				currentStage: "dashboard",
				visitedStages: ["upload"],
				lastSavedAt: new Date().toISOString(),
				xmlApplicationData: xmlApplicationData || null,
			});
			persist(state);
		},
		setSelectedPath: (state, action) => {
			state.selectedPath = action.payload;
			if (!state.visitedStages.includes("qualify")) {
				state.visitedStages.push("qualify");
			}
			state.lastSavedAt = new Date().toISOString();
			persist(state);
		},
		setQualifySubStep: (state, action) => {
			state.qualifySubStep = action.payload;
			state.lastSavedAt = new Date().toISOString();
			persist(state);
		},
		setFicoScore: (state, action) => {
			state.ficoScore = action.payload;
			state.lastSavedAt = new Date().toISOString();
			persist(state);
		},
		setLoanOriginatorId: (state, action) => {
			state.loanOriginatorId = action.payload;
			state.lastSavedAt = new Date().toISOString();
			persist(state);
		},
		setLoanProcessorId: (state, action) => {
			state.loanProcessorId = action.payload;
			state.lastSavedAt = new Date().toISOString();
			persist(state);
		},
		// Shallow-merges at the top level only (e.g. { propertyIncomeExtra: {...} } or
		// { declarationsExtra: {...} }) so setting one key never clobbers another.
		setProgressData: (state, action) => {
			state.progressData = { ...state.progressData, ...action.payload };
			state.lastSavedAt = new Date().toISOString();
			persist(state);
		},
		setAuraResult: (state, action) => {
			state.auraResult = action.payload;
			state.auraRunAt = new Date().toISOString();
			state.applicationDataChangedSinceAura = false;
			state.liabilitiesChangedSinceAura = false;
			state.lastSavedAt = new Date().toISOString();
			persist(state);
		},
		setSelectedProgram: (state, action) => {
			state.selectedProgram = action.payload;
			state.lastSavedAt = new Date().toISOString();
			persist(state);
		},
		setLockDecision: (state, action) => {
			state.lockDecision = action.payload;
			state.lastSavedAt = new Date().toISOString();
			persist(state);
		},
		setCurrentStage: (state, action) => {
			state.currentStage = action.payload;
			if (!state.visitedStages.includes(action.payload)) {
				state.visitedStages.push(action.payload);
			}
			state.lastSavedAt = new Date().toISOString();
			persist(state);
		},
		setSectionStatus: (state, action) => {
			const { section, status } = action.payload;
			state.sectionStatuses[section] = status;
			if (state.auraRunAt) {
				state.applicationDataChangedSinceAura = true;
			}
			state.lastSavedAt = new Date().toISOString();
			persist(state);
		},
		// Records a section's live validation result (Dashboard keeps these in step for
		// Submit's "Application validation complete"). Not a data change, so unlike
		// setSectionStatus it never flags the application as changed since AURA.
		syncSectionStatus: (state, action) => {
			const { section, status } = action.payload;
			if (state.sectionStatuses[section] === status) return;
			state.sectionStatuses[section] = status;
			persist(state);
		},
		setExceptionStatus: (state, action) => {
			state.exceptionStatus = action.payload;
			state.lastSavedAt = new Date().toISOString();
			persist(state);
		},
		setIncomeDocumentationType: (state, action) => {
			state.incomeDocumentationType = action.payload;
			state.lastSavedAt = new Date().toISOString();
			persist(state);
		},
		setConditions: (state, action) => {
			state.conditions = action.payload || [];
			state.lastSavedAt = new Date().toISOString();
			persist(state);
		},
		// Resets only the Qualify-path progress (credit re-issue result, selected
		// program, lock decision, AURA result) — used when the Income Documentation
		// Type changes after that work already exists, since the new doc type
		// re-evaluates required fields and prior eligibility no longer applies.
		resetQualifyProgress: (state) => {
			state.auraResult = null;
			state.auraRunAt = null;
			state.selectedProgram = null;
			state.lockDecision = null;
			state.ficoScore = null;
			state.exceptionStatus = "none";
			state.applicationDataChangedSinceAura = false;
			state.liabilitiesChangedSinceAura = false;
			state.conditions = [];
			persist(state);
		},
		markApplicationChanged: (state) => {
			state.applicationDataChangedSinceAura = true;
			persist(state);
		},
		// Liabilities were saved — only meaningful once AURA has run.
		markLiabilitiesChanged: (state) => {
			if (!state.auraRunAt) return;
			state.liabilitiesChangedSinceAura = true;
			state.applicationDataChangedSinceAura = true;
			persist(state);
		},
		setDashboardDirty: (state, action) => {
			state.dashboardDirty = !!action.payload;
		},
		// { target } — the Dashboard saves, then navigates to target; null clears it.
		setDashboardSaveRequest: (state, action) => {
			state.dashboardSaveRequest = action.payload || null;
		},
		clearCurrentLoan: (state) => {
			Object.assign(state, { ...defaultState });
			storageDelete(CURRENT_LOAN_STORAGE_KEY);
		},
		setLoanGuid: (state, action) => {
			const { loanGuid, loanNumber } = action.payload;
			state.loanGuid = loanGuid;
			if (loanNumber !== undefined) state.loanNumber = loanNumber;
			persist(state);
		},
		clearXmlApplicationData: (state) => {
			state.xmlApplicationData = null;
		},
		setQualifyInputs: (state, action) => {
			state.qualifyInputs = { ...state.qualifyInputs, ...action.payload };
			persist(state);
		},
		setProductInputs: (state, action) => {
			state.productInputs = { ...state.productInputs, ...action.payload };
			persist(state);
		},
		// Enters a *previously created* loan (e.g. clicked from Upload History) —
		// distinct from initLoan, which seeds a brand-new upload/manual entry.
		// Resets all AURA-specific fields to defaults first so a stale localStorage
		// cache from a different loan can never leak into the one being resumed;
		// hydrateProgress (below) then fills in whatever that loan actually saved.
		resumeLoan: (state, action) => {
			const { loanGuid, loanNumber, borrowerName } = action.payload;
			Object.assign(state, {
				...defaultState,
				loanGuid,
				loanNumber: loanNumber ?? null,
				borrowerName: borrowerName || "Unknown Borrower",
				currentStage: "dashboard",
				visitedStages: ["upload", "dashboard"],
				lastSavedAt: new Date().toISOString(),
			});
			persist(state);
		},
		// Applies a fetched ClearPathSession progress record onto state. Only
		// overwrites a field when the response actually included a non-null/undefined
		// value for it, so a brand-new loan with no saved progress yet (404/empty
		// response) is a no-op rather than blanking out state resumeLoan just set.
		hydrateProgress: (state, action) => {
			const data = action.payload || {};
			const fieldMap = {
				current_stage: "currentStage",
				selected_path: "selectedPath",
				selected_program: "selectedProgram",
				lock_decision: "lockDecision",
				aura_result: "auraResult",
				fico_score: "ficoScore",
				exception_status: "exceptionStatus",
				section_statuses: "sectionStatuses",
				visited_stages: "visitedStages",
				income_documentation_type: "incomeDocumentationType",
				conditions: "conditions",
				progress_data: "progressData",
			};
			Object.entries(fieldMap).forEach(([responseKey, stateKey]) => {
				const value = data[responseKey];
				if (value === null || value === undefined) return;
				// A blank saved Income Documentation Type must not wipe a value the
				// Dashboard already resolved from Encompass (CX.IncomeVerifyType) — the
				// fallback fetch and this hydration race on mount.
				if (stateKey === "incomeDocumentationType" && value === "" && state[stateKey]) return;
				state[stateKey] = value;
			});
			if (data.last_saved_at) state.lastSavedAt = data.last_saved_at;
			persist(state);
		},
		// Marks the hydration GET attempt as done (success or 404) — see
		// progressHydrated's comment in defaultState for why useSyncAuraProgress
		// gates on this instead of PATCHing immediately on mount.
		markProgressHydrated: (state) => {
			state.progressHydrated = true;
			persist(state);
		},
	},
});

export const {
	initLoan,
	setSelectedPath,
	setQualifySubStep,
	setFicoScore,
	setLoanOriginatorId,
	setLoanProcessorId,
	setProgressData,
	setAuraResult,
	setSelectedProgram,
	setLockDecision,
	setCurrentStage,
	setSectionStatus,
	syncSectionStatus,
	setExceptionStatus,
	setIncomeDocumentationType,
	setConditions,
	resetQualifyProgress,
	markApplicationChanged,
	markLiabilitiesChanged,
	setDashboardDirty,
	setDashboardSaveRequest,
	clearCurrentLoan,
	clearXmlApplicationData,
	setQualifyInputs,
	setProductInputs,
	setLoanGuid,
	resumeLoan,
	hydrateProgress,
	markProgressHydrated,
} = currentLoanSlice.actions;

export const selectLoanGuid = (state) => state.currentLoan.loanGuid;
export const selectLoanNumber = (state) => state.currentLoan.loanNumber;
export const selectBorrowerName = (state) => state.currentLoan.borrowerName;
export const selectCurrentStage = (state) => state.currentLoan.currentStage;
export const selectSelectedPath = (state) => state.currentLoan.selectedPath;
export const selectQualifySubStep = (state) => state.currentLoan.qualifySubStep;
export const selectAuraResult = (state) => state.currentLoan.auraResult;
export const selectAuraRunAt = (state) => state.currentLoan.auraRunAt;
export const selectProgressHydrated = (state) => state.currentLoan.progressHydrated;
export const selectSelectedProgram = (state) => state.currentLoan.selectedProgram;
export const selectLockDecision = (state) => state.currentLoan.lockDecision;
export const selectFicoScore = (state) => state.currentLoan.ficoScore;
export const selectLoanOriginatorId = (state) => state.currentLoan.loanOriginatorId;
export const selectLoanProcessorId = (state) => state.currentLoan.loanProcessorId;
export const selectProgressData = (state) => state.currentLoan.progressData || {};
export const selectExceptionStatus = (state) => state.currentLoan.exceptionStatus;
export const selectIncomeDocumentationType = (state) =>
	state.currentLoan.incomeDocumentationType;
export const selectConditions = (state) => state.currentLoan.conditions || [];
export const selectApplicationDataChanged = (state) =>
	state.currentLoan.applicationDataChangedSinceAura;
export const selectSectionStatuses = (state) => state.currentLoan.sectionStatuses;
export const selectLiabilitiesChangedSinceAura = (state) =>
	!!state.currentLoan.liabilitiesChangedSinceAura;
export const selectDashboardDirty = (state) => !!state.currentLoan.dashboardDirty;
export const selectDashboardSaveRequest = (state) => state.currentLoan.dashboardSaveRequest || null;
export const selectVisitedStages = (state) => state.currentLoan.visitedStages;
export const selectXmlApplicationData = (state) => state.currentLoan.xmlApplicationData;
export const selectQualifyInputs = (state) => state.currentLoan.qualifyInputs || {};
export const selectProductInputs = (state) =>
	state.currentLoan.productInputs || { ...PRODUCT_INPUTS_DEFAULTS };

export default currentLoanSlice.reducer;
